import React from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  Stack
} from '@mui/material';
import {
  CheckCircle as PublishedIcon,
  Schedule as ScheduledIcon,
  Edit as DraftIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const PostsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: '120px'
}));

const PostsSummary = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5)
}));

const PostsBreakdown = styled(Stack)(({ theme }) => ({
  direction: 'row',
  gap: theme.spacing(0.5),
  flexWrap: 'wrap'
}));

const PostChip = styled(Chip)(({ theme, variant }) => {
  const variants = {
    published: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.success.contrastText
      }
    },
    scheduled: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.warning.contrastText
      }
    },
    draft: {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[700],
      '& .MuiChip-icon': {
        color: theme.palette.grey[700]
      }
    },
    error: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.error.contrastText
      }
    }
  };

  return {
    height: '20px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    ...variants[variant],
    '& .MuiChip-label': {
      padding: '0 6px'
    }
  };
});

const PostsIndicator = ({ googlePosts = {}, totalPosts = 0, publishedPosts = 0, scheduledPosts = 0, draftPosts = 0, failedPosts = 0 }) => {
  // Use Google Posts data if available, fallback to individual props
  const posts = {
    published: googlePosts.published || publishedPosts,
    scheduled: googlePosts.scheduled || scheduledPosts,
    drafts: googlePosts.drafts || draftPosts,
    failed: failedPosts,
    total: googlePosts.total || totalPosts,
    lastSynced: googlePosts.lastSynced,
    error: googlePosts.error
  };

  if (posts.total === 0) {
    return (
      <PostsContainer>
        <Typography variant="body2" color="text.secondary">
          Keine Posts
        </Typography>
      </PostsContainer>
    );
  }

  return (
    <PostsContainer>
      {/* Summary */}
      <PostsSummary>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
          {posts.total}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Posts
        </Typography>
        {posts.error && (
          <Tooltip title={`Fehler beim Laden: ${posts.error}`} arrow>
            <ErrorIcon color="error" sx={{ fontSize: 16 }} />
          </Tooltip>
        )}
      </PostsSummary>

      {/* Breakdown */}
      <PostsBreakdown direction="row">
        {posts.published > 0 && (
          <Tooltip 
            title={`${posts.published} veröffentlichte Posts${posts.lastSynced ? ` (Stand: ${new Date(posts.lastSynced).toLocaleString('de-DE')})` : ''}`} 
            arrow
          >
            <PostChip
              icon={<PublishedIcon />}
              label={posts.published}
              variant="published"
              size="small"
            />
          </Tooltip>
        )}
        
        {posts.scheduled > 0 && (
          <Tooltip title={`${posts.scheduled} geplante Posts`} arrow>
            <PostChip
              icon={<ScheduledIcon />}
              label={posts.scheduled}
              variant="scheduled"
              size="small"
            />
          </Tooltip>
        )}
        
        {posts.drafts > 0 && (
          <Tooltip title={`${posts.drafts} Entwürfe`} arrow>
            <PostChip
              icon={<DraftIcon />}
              label={posts.drafts}
              variant="draft"
              size="small"
            />
          </Tooltip>
        )}
        
        {posts.failed > 0 && (
          <Tooltip title={`${posts.failed} fehlgeschlagene Posts`} arrow>
            <PostChip
              icon={<ErrorIcon />}
              label={posts.failed}
              variant="error"
              size="small"
            />
          </Tooltip>
        )}
      </PostsBreakdown>
    </PostsContainer>
  );
};

export default PostsIndicator;